import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="form-validation"
export default class extends Controller {
  fields = {}; // map from field ID to corresponding DOM nodes (we support multiple for the case of radio buttons)
  valid = {}; // map from field ID to true/false indicating if it's valid

  static targets = ["invalidMessage"]

  initialize() {
    this.boundUpdateAndValidate = this.updateAndValidate.bind(this)
  }

  getSubmitButtons() {
    var submitButtons = Array.from(this.element.querySelectorAll("button[type=submit], button[type=action]"))
    if (this.element.id) {
      const formButtons = Array.from(document.querySelectorAll("button[form=" + this.element.id + "]"))
      submitButtons = submitButtons.concat(formButtons)
    }
    return submitButtons
  }

  connect() {
    // Set the submit button to disabled
    var submitButtons = this.getSubmitButtons()
    for (var i = 0; i < submitButtons.length; i++) {
      submitButtons[i].disabled = true
    }

    const allElements = [];

    // Find all the elements, add listeners, and run validation on each (in case the form is pre-loaded with values)
    var elementIds = this.element.getAttribute("data-required-form-element-ids").split(",");
    for (var i = 0; i < elementIds.length; i++) {
      var elementId = elementIds[i].trim();
      if (elementId === undefined || elementId === "") {
        continue;
      }

      this.valid[elementId] = false;

      var element = document.getElementById(elementId);
      if (element === null) {
        // Special handling for radio buttons: instead of finding the item by ID, we need to find it by name, and we
        // we need to add all of the individual radio buttons to the list of elements.
        var subElements = document.getElementsByName(elementId);

        // Further special handling for checkboxes -- they allow multiple values by appding "[]" to the name.
        if (subElements.length == 0) {
          subElements = document.getElementsByName(elementId + "[]");
        }

        // Further special handling for dates.
        if (subElements.length == 0) {
          subElements = [];
          var allSelectInputs = this.element.querySelectorAll("select");
          for (var j = 0; j < allSelectInputs.length; j++) {
            const select = allSelectInputs[j];
            if (select.getAttribute("name").indexOf(elementId) != -1 || select.id.indexOf(elementId) != -1) {
              subElements.push(select);
            }
          }
        }

        this.fields[elementId] = subElements;

        for (var j = 0; j < subElements.length; j++) {
          const subElement = subElements[j];
          allElements.push(subElement);
        }
      } else {
        this.fields[elementId] = [element];
        allElements.push(element)
      }
    }

    for (var i = 0; i < allElements.length; i++) {
      const element = allElements[i];
      this.addListenersToElement(element);
      // Create a fake event to trigger the updateAndValidate method.
      element.dispatchEvent(new Event("change"));
    }

    this.validateForm();

    // Add asterisks to labels for required fields
    var updatedLabels = [];
    for (var i = 0; i < allElements.length; i++) {
      const element = allElements[i];

      var labelFor = element.id;

      // Special handling for checkboxes, radio buttons, and date fields.
      if (element.type == "checkbox" || element.type == "radio") {
        labelFor = element.name;

        // Checkboxes allow multiple values by adding "[]" to the field name, but the corresponding label
        // doesn't have that suffix.
        if (labelFor.endsWith("[]")) {
          labelFor = labelFor.substring(0, labelFor.length - 2);
        }
      } else if (labelFor.endsWith("_1i")) {
        var name = element.getAttribute("name");
        name = name.replace(/\(\d+i\)/, '');
        labelFor = name.substring(1, name.length - 1);

        // If we don't find a match by name, try by ID instead.
        if (document.querySelector(`label[for="${labelFor}"]`) === null) {
          labelFor = element.id.split("_").slice(0, -1).join("_");
        }
      }

      const label = document.querySelector(`label[for="${labelFor}"]`);
      if (label && updatedLabels.indexOf(label) == -1 && !label.querySelector(".required-asterisk")) {
        label.insertAdjacentHTML("beforeend", ' <span class="required-asterisk text-alert-red-500">*</span>');
        updatedLabels.push(label);
      }
    }
  }

  addListenersToElement(element) {
    // Ensure we set up an event to watch for change OR keyups and then ensure they have values.
    element.addEventListener("keyup", this.boundUpdateAndValidate)
    element.addEventListener("change", this.boundUpdateAndValidate)
  }

  disconnect() {
    for (const [elementId, elements] of Object.entries(this.fields)) {
      for (var i = 0; i <= elements.length; i++) {
        const element = elements[i];
        if (element && element.removeEventListener) {
          element.removeEventListener("keyup", this.boundUpdateAndValidate);
          element.removeEventListener("change", this.boundUpdateAndValidate);
        }
      }
    }
  }

  updateAndValidate(event) {
    var elementId = event.target.id;
    if (event.target.type == 'radio' || event.target.type == 'checkbox') {
      elementId = event.target.name;
    }

    if (event.target.type === "radio") {
      if (event.target.checked) {
        // For radio buttons, as soon as any of the options is picked, it should be considered valid.
        this.valid[elementId] = true;
      }
    } else if (event.target.type == "checkbox") {
      // For checkboxes, as soon as any of the options is picked, it should be considered valid.
      var name = event.target.getAttribute("name");
      if (name.endsWith("[]")) {
        name = name.substring(0, name.length - 2);
      }
      var options = this.fields[name];
      var selected = 0;
      for (var i = 0; i < options.length; i++) {
        const option = options[i];
        if (option.checked) {
          selected++;
        }
      }
      if (selected > 0) {
        this.valid[name] = true;
      } else {
        this.valid[name] = false;
      }
    } else if (/^.*_\d+i$/.test(elementId)) {
      // This is the pattern for rails date fields.  There's a hidden field that stores the full date,
      // and there are individual select elements that capture each piece (month / date / year).  The
      // hidden field doesn't get a value until very late in the submission process, so instead we
      // need to check if all of the sub-elements have a value.  If so, then we also mark the hidden
      // element as valid.

      // First mark the individual select element as valid or not.
      if (event.target.value === undefined || event.target.value === "") {
        this.valid[elementId] = false;
      } else {
        this.valid[elementId] = true;
      }

      // Now look for the corresponding hidden element and mark it valid if ALL the sub-elements are valid.
      var hiddenFieldName = event.target.getAttribute("name");
      hiddenFieldName = hiddenFieldName.replace(/\(\d+i\)/, '');
      hiddenFieldName = hiddenFieldName.substring(1, hiddenFieldName.length - 1);

      if (this.fields[hiddenFieldName] === undefined) {
        // If we don't find a match by name, try by ID instead.
        hiddenFieldName = event.target.id.split("_").slice(0, -1).join("_");
      }

      var selectElementsWithValue = 0;
      for (var i = 0; i < this.fields[hiddenFieldName].length; i++) {
        const selectElement = this.fields[hiddenFieldName][i];
        if (selectElement.value != "") {
          selectElementsWithValue++;
        }
      }
      if (selectElementsWithValue == this.fields[hiddenFieldName].length) {
        this.valid[hiddenFieldName] = true;
      } else {
        this.valid[hiddenFieldName] = false;
      }
    } else if (event.target.value === undefined || event.target.value === "") {
      this.valid[elementId] = false;
    } else {
      this.valid[elementId] = true;
    }
    this.validateForm();
  }

  validateForm() {
    const firstFalseIndex = Object.values(this.valid).findIndex((value) => value == false);
    const formIsValid = (firstFalseIndex == -1);
    // set the submit button to enabled
    var submitButtons = this.getSubmitButtons()
    for (var i = 0; i < submitButtons.length; i++) {
      submitButtons[i].disabled = !formIsValid
    }

    if (this.hasInvalidMessageTarget) {
      if (formIsValid) {
        this.invalidMessageTarget.classList.add("hidden")
      } else {
        this.invalidMessageTarget.classList.remove("hidden")
      }
    }
  }
}
